import { takeLatest, all, fork, call, put } from "redux-saga/effects";

import {
  REGISTER_ACCOUNTS_USER,
  ONBOARDING_HYDRATE_REQUEST,
  ONBOARDING_SAVE_BUSINESS_REQUEST,
  ONBOARDING_SAVE_PRODUCTION_REQUEST,
  ONBOARDING_SAVE_SALES_REQUEST,
  ONBOARDING_FINALIZE_REQUEST,
  ONBOARDING_SAVE_BRANDING_REQUEST,
  ONBOARDING_SAVE_PRICING_REQUEST,
  ONBOARDING_SAVE_PRODUCTS_REQUEST,
  ONBOARDING_SAVE_PRINT_OPTIONS_REQUEST,
  ONBOARDING_SAVE_STORE_REQUEST,
  ONBOARDING_ENRICH_WEBSITE_REQUEST,
} from "../../constants/actionTypes";
import {
  registerAccountsUserSuccess,
  registerAccountsUserFailure,
  hydrateOnboardingSuccess,
  hydrateOnboardingFailure,
  saveBusinessProfileSuccess,
  saveBusinessProfileFailure,
  saveProductionSuccess,
  saveProductionFailure,
  saveSalesSuccess,
  saveSalesFailure,
  finalizeOnboardingSuccess,
  finalizeOnboardingFailure,
  saveOnboardingBrandingSuccess,
  saveOnboardingBrandingFailure,
  saveOnboardingPricingSuccess,
  saveOnboardingPricingFailure,
  saveOnboardingProductsSuccess,
  saveOnboardingProductsFailure,
  saveOnboardingPrintOptionsSuccess,
  saveOnboardingPrintOptionsFailure,
  saveOnboardingStoreSuccess,
  saveOnboardingStoreFailure,
  enrichWebsiteSuccess,
  enrichWebsiteFailure,
} from "../../actions/onboarding";
import {
  registerAccountsUser as registerAccountsUserApi,
  initOnboarding,
  getOnboardingState,
  saveBusinessProfile as saveBusinessProfileApi,
  saveProduction as saveProductionApi,
  saveSales as saveSalesApi,
  finalizeOnboarding as finalizeOnboardingApi,
  getSignInToken,
  saveOnboardingBranding as saveOnboardingBrandingApi,
  saveOnboardingPricing as saveOnboardingPricingApi,
  saveOnboardingProducts as saveOnboardingProductsApi,
  saveOnboardingPrintOptions as saveOnboardingPrintOptionsApi,
  saveOnboardingStore as saveOnboardingStoreApi,
  enrichWebsite as enrichWebsiteApi,
} from "../../api/onboarding";
import { getFirebaseBackend } from "../../helpers/firebase_helper";
import {
  RIVERR_ADS_EVENTS,
  trackRiverrAdsEvent,
} from "../../helpers/riverrAdsTracking";

const extractMessage = (error) =>
  error?.response?.data?.message ||
  error?.message ||
  "Something went wrong. Please try again.";

/**
 * Step 1 — founder signup.
 *
 * Hits POST /accounts/register (NOT /auth/signup). The backend creates
 * a Firebase Auth user with no enterprise attached, returning a custom
 * token for client-side sign-in. On success we also call
 * /onboarding/init so the wizard doc exists by the time Step 2 mounts.
 *
 * Failure path keeps the user signed-out and surfaces a friendly
 * message via the reducer; no router redirect on failure so the form
 * stays interactive.
 */
function* registerAccounts({ payload }) {
  try {
    const response = yield call(registerAccountsUserApi, payload);
    const data = response?.data || {};
    if (response?.data?.status >= 400 || response?.data?.error) {
      throw new Error(data?.message || "Signup failed");
    }
    const { customToken, user } = data;
    if (!customToken) throw new Error("Signup returned no auth token");

    const fb = getFirebaseBackend();
    yield call(fb.loginUser, null, null, customToken);

    // Best-effort onboarding-doc init. We don't fail the whole flow
    // if this returns non-2xx; the wizard hydrate on Step 2 will
    // self-heal.
    try {
      yield call(initOnboarding);
    } catch (initErr) {
      console.warn("onboarding init failed (non-fatal):", initErr);
    }

    yield put(registerAccountsUserSuccess(user));
    // Pixel only — CAPI already fired from POST /accounts/register with
    // the same event_id for Meta dedupe.
    trackRiverrAdsEvent(RIVERR_ADS_EVENTS.COMPLETE_REGISTRATION, {
      email: payload?.email || user?.email,
      eventId: payload?.event_id || data?.eventId,
      customData: { signup_source: payload?.signup_source },
      skipCapi: true,
    });
    window.location.href = "/onboarding/business";
  } catch (error) {
    console.error("Accounts register error:", error);
    yield put(registerAccountsUserFailure(extractMessage(error)));
  }
}

function* hydrateState() {
  try {
    // Ensure the doc exists before fetching it — first-time visit to
    // /onboarding/business after a Google signup that didn't run init.
    try {
      yield call(initOnboarding);
    } catch (initErr) {
      // Init is idempotent; transient errors here aren't fatal.
      console.warn("onboarding init failed during hydrate:", initErr);
    }
    const response = yield call(getOnboardingState);
    yield put(hydrateOnboardingSuccess(response?.data || {}));
  } catch (error) {
    yield put(hydrateOnboardingFailure(extractMessage(error)));
  }
}

function* saveBusiness({ payload }) {
  try {
    const response = yield call(saveBusinessProfileApi, payload);
    yield put(saveBusinessProfileSuccess(response?.data?.business || payload));
  } catch (error) {
    yield put(saveBusinessProfileFailure(extractMessage(error)));
  }
}

function* saveProductionStep({ payload }) {
  try {
    const response = yield call(saveProductionApi, payload);
    yield put(saveProductionSuccess(response?.data?.production || payload));
  } catch (error) {
    yield put(saveProductionFailure(extractMessage(error)));
  }
}

function* saveSalesStep({ payload }) {
  try {
    const response = yield call(saveSalesApi, payload);
    yield put(saveSalesSuccess(response?.data?.sales || payload));
  } catch (error) {
    yield put(saveSalesFailure(extractMessage(error)));
  }
}

function* finalize({ payload }) {
  try {
    const response = yield call(finalizeOnboardingApi, payload);
    const result = response?.data || {};
    yield put(finalizeOnboardingSuccess(result));
    trackRiverrAdsEvent(RIVERR_ADS_EVENTS.STORE_CREATED, {
      customData: { hostname: result?.hostname },
    });

    // The freshly-created enterprise's ONLY hostname is
    // `{subdomain}.myriverr.com`. Staying on accounts.riverr.app trips
    // the VerticalLayout domain guard ("Not Authorized to access this
    // domain"), so we hop to the tenant subdomain. The UI parks on
    // `/onboarding/complete` during this mint+hop so /dashboard never
    // mounts on the accounts domain. Firebase sessions are origin-scoped
    // and don't carry across domains, so we mint a fresh custom token and
    // let the tenant domain's `/onboarding` handler sign the founder in
    // (it then lands on /dashboard).
    // "Build my store" routes the founder to store creation on the tenant
    // instead of the dashboard. We can't pass router state across the domain
    // hop, so the intent rides along as `?next=create-store`; the tenant's
    // `/onboarding` landing reads it and redirects there after sign-in.
    const createStore = !!payload?.createStore;
    const nextParam = createStore ? "&next=create-store" : "";

    const hostname = result?.hostname;
    if (hostname) {
      let target = `https://${hostname}/login`;
      try {
        const tokenRes = yield call(getSignInToken);
        const token = tokenRes?.data?.token;
        if (token) {
          target = `https://${hostname}/onboarding?customToken=${encodeURIComponent(
            token,
          )}${nextParam}`;
        }
      } catch (tokenErr) {
        console.warn("finalize: custom-token mint failed:", tokenErr);
      }
      window.location.href = target;
      return;
    }

    // Fallback for backends that don't return a hostname yet (same-origin).
    if (createStore) {
      window.location.href = "/shop/settings?fromOnboarding=1";
      return;
    }
    window.location.href = result?.tenantUrl || `/pricing?welcome=true`;
  } catch (error) {
    yield put(finalizeOnboardingFailure(extractMessage(error)));
  }
}

function* saveBrandingStep({ payload }) {
  try {
    const response = yield call(saveOnboardingBrandingApi, payload);
    const data = response?.data || {};
    yield put(
      saveOnboardingBrandingSuccess({
        branding: data.branding || payload,
        enterpriseId: data.enterpriseId || null,
      }),
    );
  } catch (error) {
    yield put(saveOnboardingBrandingFailure(extractMessage(error)));
  }
}

function* savePricingStep({ payload }) {
  try {
    const response = yield call(saveOnboardingPricingApi, payload);
    const data = response?.data || {};
    yield put(
      saveOnboardingPricingSuccess({
        pricingTableIds: data.pricingTableIds || payload?.pricingTableIds || [],
      }),
    );
  } catch (error) {
    yield put(saveOnboardingPricingFailure(extractMessage(error)));
  }
}

function* saveProductsStep({ payload }) {
  try {
    const response = yield call(saveOnboardingProductsApi, payload);
    const data = response?.data || {};
    yield put(
      saveOnboardingProductsSuccess({
        blankProducts: data.blankProducts || payload?.blankProducts || [],
      }),
    );
  } catch (error) {
    yield put(saveOnboardingProductsFailure(extractMessage(error)));
  }
}

function* savePrintOptionsStep({ payload }) {
  try {
    const response = yield call(saveOnboardingPrintOptionsApi, payload);
    const data = response?.data || {};
    yield put(
      saveOnboardingPrintOptionsSuccess({
        categoryConfig: data.categoryConfig || payload?.categoryConfig || {},
      }),
    );
  } catch (error) {
    yield put(saveOnboardingPrintOptionsFailure(extractMessage(error)));
  }
}

function* saveStoreStep({ payload }) {
  try {
    const response = yield call(saveOnboardingStoreApi, payload);
    const data = response?.data || {};
    yield put(
      saveOnboardingStoreSuccess({
        createStore: data.createStore ?? payload?.createStore ?? false,
      }),
    );
  } catch (error) {
    yield put(saveOnboardingStoreFailure(extractMessage(error)));
  }
}

/**
 * Best-effort website enrichment. Fired (debounced) when the founder enters
 * their existing website on the Business step. Failure is silent — the wizard
 * never depends on this; it only ever ADDS suggestions the user can override.
 */
function* enrichWebsiteStep({ payload }) {
  try {
    const response = yield call(enrichWebsiteApi, payload);
    const enrichment = response?.data?.enrichment || null;
    if (enrichment) {
      yield put(enrichWebsiteSuccess(enrichment));
    } else {
      yield put(enrichWebsiteFailure(null));
    }
  } catch (error) {
    // Silent — enrichment is a nice-to-have, not a required step.
    yield put(enrichWebsiteFailure(extractMessage(error)));
  }
}

function* watchOnboarding() {
  yield takeLatest(REGISTER_ACCOUNTS_USER, registerAccounts);
  yield takeLatest(ONBOARDING_HYDRATE_REQUEST, hydrateState);
  yield takeLatest(ONBOARDING_SAVE_BUSINESS_REQUEST, saveBusiness);
  yield takeLatest(ONBOARDING_SAVE_PRODUCTION_REQUEST, saveProductionStep);
  yield takeLatest(ONBOARDING_SAVE_SALES_REQUEST, saveSalesStep);
  yield takeLatest(ONBOARDING_FINALIZE_REQUEST, finalize);
  yield takeLatest(ONBOARDING_SAVE_BRANDING_REQUEST, saveBrandingStep);
  yield takeLatest(ONBOARDING_SAVE_PRICING_REQUEST, savePricingStep);
  yield takeLatest(ONBOARDING_SAVE_PRODUCTS_REQUEST, saveProductsStep);
  yield takeLatest(ONBOARDING_SAVE_PRINT_OPTIONS_REQUEST, savePrintOptionsStep);
  yield takeLatest(ONBOARDING_SAVE_STORE_REQUEST, saveStoreStep);
  yield takeLatest(ONBOARDING_ENRICH_WEBSITE_REQUEST, enrichWebsiteStep);
}

export default function* onboardingSaga() {
  yield all([fork(watchOnboarding)]);
}
